import style from './ActionButton.module.scss';

import { translate } from '@chaika/i18n/helper';

import { EntryPageTestAttributes } from 'chaika-test-attributes';

import { CertificateSignForm } from '@common/CertificateSignForm';

import { SelectButtonMenu, SelectButtonMenuItem } from '@components/SelectButtonMenu';
import { Popup } from '@components/Popup';
import { Input } from '@components/Input';
import { CancelConfirmButtons } from '@components/ActionButtons/CancelConfirmButtons';
import { SelectButtonList } from '@components/SelectButtonList';
import { SelectButtonListModifiers } from '@components/SelectButtonList/SelectButtonList';
import { Button, ButtonModifiers, ButtonTheme } from '@components/Button';

export interface ActionButtonContentParams {
    modifiers?: SelectButtonListModifiers[];
}

interface ActionButtonContentProps extends ActionButtonContentParams {
    items: SelectButtonMenuItem[];
    title: string;
    isMobile: boolean;
    isViewMode: boolean;
    primaryActionDisabled?: boolean;
    isShowingEmailInputPopup: boolean;
    isShowingEDSPopup: boolean;
    email?: string;
    onEmailInput(event: React.ChangeEvent<HTMLInputElement>): void;
    onConfirmEmailSending(): void;
    onCancelEmailSending(): void;
    onButtonClick(): void;
    onCloseEDSPopup(): void;
}

export function ActionButtonContent({
    items,
    title,
    primaryActionDisabled,
    isShowingEmailInputPopup = false,
    isShowingEDSPopup,
    email = '',
    onEmailInput,
    onConfirmEmailSending,
    onCancelEmailSending,
    onButtonClick,
    onCloseEDSPopup,
    isMobile,
    modifiers,
    isViewMode,
}: ActionButtonContentProps) {
    const tButtonSignLabel = translate('publish');
    const tButtonEditLabel = translate('edit');

    let selectButton;

    if (isMobile && modifiers?.includes(SelectButtonListModifiers.Header)) {
        selectButton = (
            <div className={style.headerButton}>
                <SelectButtonList
                    items={items}
                    modifiers={modifiers}
                    dataTest={EntryPageTestAttributes.HeaderList}
                />
            </div>
        );
    } else if (isMobile && modifiers?.includes(SelectButtonListModifiers.Pair)) {
        selectButton = (
            <div className={style.pairButtons}>
                <Button
                    label={isViewMode ? tButtonEditLabel : tButtonSignLabel}
                    dataTest={isViewMode ? EntryPageTestAttributes.EditButton : EntryPageTestAttributes.SignButton}
                    theme={ButtonTheme.Main}
                    onClick={onButtonClick}
                    modifiers={[ButtonModifiers.InheritWidth]}
                />
                <SelectButtonList
                    items={items}
                    modifiers={modifiers}
                    dataTest={EntryPageTestAttributes.HeaderList}
                />
            </div>
        );
    } else {
        selectButton = (
            <SelectButtonMenu
                primaryActionDisabled={primaryActionDisabled}
                title={title}
                items={items}
                onButtonClick={onButtonClick}
                dataTestButton={isViewMode ? EntryPageTestAttributes.EditButton : EntryPageTestAttributes.SignButton}
                dataTestList={EntryPageTestAttributes.HeaderList}
            />
        );
    }

    return (
        <div className={style.root}>
            {isShowingEmailInputPopup && (
                <Popup onOutOfContentClick={onCancelEmailSending}>
                    <div className={style.emailPopup}>
                        <span className={style.emailPopupTitle}>{translate('sendEntryByEmail')}</span>
                        <Input
                            dataTest={EntryPageTestAttributes.Email}
                            value={email}
                            type={'email'}
                            placeholder={'email...'}
                            onChange={onEmailInput}
                        />
                        <CancelConfirmButtons
                            withoutSideMargins
                            dataTest={EntryPageTestAttributes.SendEmailButton}
                            confirmText={translate('send')}
                            cancelText={translate('cancel')}
                            disableConfirmButton={email.length === 0}
                            onConfirm={onConfirmEmailSending}
                            onCancel={onCancelEmailSending}
                        />
                    </div>
                </Popup>
            )}
            {isShowingEDSPopup && <CertificateSignForm onCloseClick={onCloseEDSPopup} />}
            {selectButton}
        </div>
    );
}
